import React from 'react'
import styled from 'styled-components/native'
import moment from 'moment'

import Typography from 'APP/Converse/Typography'
import Button from 'APP/Converse/Button'
import AppointmentCardStatic from 'APP/Components/AppointmentCard/AppointmentCardStatic'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import { useAddToCalendar } from 'APP/Hooks/useAddToCalendar'
import I18n from 'APP/I18n'

export interface BookingConfirmationProps {
  memberName: string
  providerName: string
  providerRole?: string
  providerPicture?: string
  startAt: string
  endAt: string
  episodeId?: string
  issueTypeId?: string
  lateChangeFee?: number
  onClose: () => void
  testID?: string
}

const Container = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`

const ScrollContainer = styled(ResponsiveScrollView)`
  flex: 1;
`

const Content = styled.View`
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const TitleSection = styled.View`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const FeeNotice = styled(Typography).attrs({
  variant: 'bodyNormal',
  color: 'textSecondary',
})`
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const Title = styled(Typography).attrs({
  variant: 'h1',
})`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

const Footer = styled(ResponsiveView)`
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const BookingConfirmation = ({
  memberName,
  providerName,
  providerRole,
  providerPicture,
  startAt,
  endAt,
  episodeId,
  issueTypeId,
  lateChangeFee,
  onClose,
  testID = 'BOOKING_CONFIRMATION',
}: BookingConfirmationProps) => {
  const timeslot = `${moment(startAt).format('LT')} - ${moment(endAt).format('LT')}`

  const addToCalendar = useAddToCalendar({
    startAt,
    endAt,
    episodeId,
    isVirtual: true,
    analyticsName: 'bookingConfirmation.addToCalendar',
  })

  return (
    <Container testID={testID}>
      <ScrollContainer
        footer={
          <Footer>
            <Button
              variant="primary"
              widthVariant="full"
              title={I18n.t('BookingConfirmation.finish')}
              onPress={onClose}
              testID={`${testID}_FINISH_BUTTON`}
              analyticsName="bookingConfirmation.finish"
            />
            <Button
              variant="secondary"
              widthVariant="full"
              title={I18n.t('Appointments.addToCalendar')}
              onPress={addToCalendar}
              testID={`${testID}_ADD_TO_CALENDAR_BUTTON`}
              analyticsName="bookingConfirmation.addToCalendar"
            />
          </Footer>
        }
      >
        <Content>
          <TitleSection>
            <Title testID={`${testID}_TITLE`}>
              {I18n.t('BookingConfirmation.title', { memberName, providerName })}
            </Title>
          </TitleSection>

          <AppointmentCardStatic
            date={startAt}
            timeslot={timeslot}
            icon={providerPicture}
            providerName={providerName}
            providerRole={providerRole}
            issueTypeId={issueTypeId}
            testID={`${testID}_APPOINTMENT_CARD`}
          />

          {!!lateChangeFee && (
            <FeeNotice testID={`${testID}_FEE_NOTICE`}>
              {I18n.t('BookingConfirmation.feeNotice', { fee: lateChangeFee })}
            </FeeNotice>
          )}
        </Content>
      </ScrollContainer>
    </Container>
  )
}

export default BookingConfirmation
